<template>
  <div class="oauth-loading-container" :class="{ 'dark-mode': isDarkMode }">
    <!-- 加载卡片容器 -->
    <div class="loading-card">
      <!-- ElementUI 加载动画 -->
      <i class="el-icon-loading loading-icon"></i>

      <!-- 主提示文字 -->
      <h3 class="loading-title">正在登录中</h3>

      <!-- 辅助提示文字 -->
      <p class="loading-desc">请稍候，系统正在完成身份验证...</p>

      <!-- 装饰性分割线 -->
      <div class="loading-divider"></div>

      <!-- 底部提示 -->
      <p class="loading-footer">请勿刷新页面，以免登录失败</p>
    </div>
  </div>
</template>

<script>
import { clearMicroToken, setIpUserFlag, setMainToken, setUserDisplayName } from "../utils/authSession";
export default {
  name: "OauthLoading",
  data() {
    return {
      isDarkMode: false, // 控制夜间模式
    };
  },
  mounted() {
    // 1. 读取夜间模式配置
    const savedMode = localStorage.getItem("darkMode");
    this.isDarkMode = savedMode === "true" || savedMode === true;

    // 2. 解析URL参数（兼容hash模式和history模式）
    const { code, state } = this.getUrlParams();

    // 4. 校验参数，缺失则提示登录异常
    if (!code || !state) {
      this.$message.error({
        message: "登录异常：缺少必要的验证参数",
        duration: 0, // 不自动关闭
        showClose: true,
        type: "error",
      });
      // 可以在此处直接阻断，页面就停留在 OauthLoading 并且显示错误，不会往下走 login()
      return;
    } else {
      this.login(code, state);
    }
  },
  methods: {
    parseState(state) {
      // 边界处理：若state不是字符串或为空，直接返回空字符串
      if (typeof state !== "string" || state.trim() === "") {
        return "";
      }

      // 查找#号的位置
      const hashIndex = state.indexOf("#");

      // 存在#号则截取前半段，否则返回原字符串
      return hashIndex > -1 ? state.substring(0, hashIndex) : state;
    },
    /**
     * 解析URL参数（兼容hash模式）
     * @returns {Object} 包含code和state的参数对象
     */
    getUrlParams() {
      // 1. 获取完整URL（兼容hash模式：#/path?key=value）
      const url = window.location.href;
      // 2. 提取?后的参数部分（兼容hash和非hash模式）
      const paramStr = url.includes("?") ? url.split("?")[1] : "";
      if (!paramStr) return { code: "", state: "" };

      // 3. 解析参数为键值对
      const params = {};
      paramStr.split("&").forEach(item => {
        const [key, value] = item.split("=");
        if (key && value) {
          params[key] = decodeURIComponent(value); // 解码特殊字符
        }
      });

      // 4. 返回code和state（空字符串表示未获取到）
      return {
        code: params.code || "",
        state: params.state || "",
      };
    },
    login(code, state) {
      let state_clean = this.parseState(state); // 剔除vue的#号可能造成的影响
      // code和state传给后端
      this.$http.get(this.globalUrl.oauth_authorization, { params: { code: code, state: state_clean } }).then(res => {
        if (res.code == -1)
          this.$message.error({
            message: res.msg,
            duration: 0, // 不自动关闭
            showClose: true,
            type: "error",
          });
        else {
          // 身份升级时原 IP 子应用 token 必须失效，后续由新主 token 换取。
          clearMicroToken();
          setIpUserFlag(null);
          window.tokenReady = false;
          setMainToken(res.data.app_token);
          setUserDisplayName(res.data.user_display_name);
          if (res.data.to_uri) {
            window.location.href = this.appendTokenToUri(res.data.to_uri, res.data.app_token_encrypt);
            return;
          }
          this.$router.replace({ path: "/literatureSearch" });
        }
      });
    },
    appendTokenToUri(uri, token) {
      const separator = uri.includes("?") ? "&" : "?";
      return `${uri}${separator}token=${encodeURIComponent(token || "")}`;
    },
  },
};
</script>

<style scoped>
/* 基础样式 - 浅色模式 */
.oauth-loading-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  transition: background 0.3s ease;
}

/* 夜间模式样式 */
.oauth-loading-container.dark-mode {
  background: linear-gradient(135deg, #1a1c23 0%, #2d3748 100%);
}

/* 加载卡片 - 浅色模式 */
.loading-card {
  width: 420px;
  padding: 60px 40px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;
}

/* 夜间模式卡片样式 */
.dark-mode .loading-card {
  background: #2d3748;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

/* 响应式适配 */
@media (max-width: 576px) {
  .loading-card {
    width: 90%;
    padding: 40px 20px;
  }
}

/* 加载图标样式 */
.loading-icon {
  font-size: 48px;
  color: #409eff;
  margin-bottom: 24px;
  animation: rotate 2s linear infinite;
  /* 夜间模式图标颜色微调 */
  transition: color 0.3s ease;
}
.dark-mode .loading-icon {
  color: #66b1ff;
}

/* 标题样式 */
.loading-title {
  font-size: 20px;
  color: #303133;
  margin: 0 0 12px 0;
  font-weight: 600;
  transition: color 0.3s ease;
}
.dark-mode .loading-title {
  color: #e5e7eb;
}

/* 描述文字 */
.loading-desc {
  font-size: 14px;
  color: #606266;
  margin: 0 0 30px 0;
  line-height: 1.6;
  transition: color 0.3s ease;
}
.dark-mode .loading-desc {
  color: #a0aec0;
}

/* 分割线 */
.loading-divider {
  width: 60px;
  height: 2px;
  background: #e4e7ed;
  margin: 0 auto 20px;
  border-radius: 1px;
  transition: background 0.3s ease;
}
.dark-mode .loading-divider {
  background: #4a5568;
}

/* 底部提示 */
.loading-footer {
  font-size: 12px;
  color: #909399;
  margin: 0;
  transition: color 0.3s ease;
}
.dark-mode .loading-footer {
  color: #718096;
}

/* 旋转动画 */
@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>

<!-- 新增非scoped样式，专门修复弹框层级 -->
<style>
/* 强制提升ElementUI消息组件层级，高于加载容器的9999 */
.el-message {
  z-index: 10000 !important;
  /* 可选：确保弹框不被其他样式覆盖 */
  /* position: fixed !important;
  top: 20px !important;
  right: 20px !important; */
}
</style>
